import { makeApiCall } from '../core';
import type {
  ItemEnvelope,
  ListEnvelope,
  SubcontractorCompany,
  SubcontractorPayStructure,
  SubcontractorVisitsReport,
} from '@ruta/shared';

// =====================
// Subcontractor Company Management
// =====================

export const getSubcontractorCompanies = async (): Promise<{ companies: SubcontractorCompany[]; count: number }> => {
  const response = await makeApiCall<ListEnvelope<SubcontractorCompany>>(
    'GET',
    '/subcontractor-companies',
  );
  return { companies: response.data, count: response.data.length };
};

export const createSubcontractorCompany = async (data: {
  name: string;
  payStructure?: SubcontractorPayStructure;
}): Promise<{ company: SubcontractorCompany }> => {
  const response = await makeApiCall<ItemEnvelope<SubcontractorCompany>>(
    'POST',
    '/subcontractor-companies',
    data,
  );
  return { company: response.data };
};

export const updateSubcontractorCompany = async (
  companyId: string,
  data: {
    name?: string;
    payStructure?: SubcontractorPayStructure | null;
  },
): Promise<{ company: SubcontractorCompany }> => {
  const response = await makeApiCall<ItemEnvelope<SubcontractorCompany>>(
    'PATCH',
    `/subcontractor-companies/${companyId}`,
    data,
  );
  return { company: response.data };
};

export const deleteSubcontractorCompany = async (companyId: string): Promise<void> => {
  await makeApiCall<void>('DELETE', `/subcontractor-companies/${companyId}`);
};

export const getSubcontractorCompanyVisits = async (
  companyId: string,
  params: { from: string; to: string },
): Promise<SubcontractorVisitsReport> => {
  const qs = new URLSearchParams({ from: params.from, to: params.to }).toString();
  const response = await makeApiCall<ItemEnvelope<SubcontractorVisitsReport>>(
    'GET',
    `/subcontractor-companies/${companyId}/visits?${qs}`,
  );
  return response.data;
};

/** Mark whole work days in [from, to] as paid for a subcontractor company. */
export const markSubcontractorDayPayments = async (
  companyId: string,
  params: { from: string; to: string },
): Promise<{ markedDayCount: number; days: string[] }> => {
  const response = await makeApiCall<
    ItemEnvelope<{ companyId: string; from: string; to: string; markedDayCount: number; days: string[] }>
  >('POST', `/subcontractor-companies/${companyId}/day-payments`, params);
  return { markedDayCount: response.data.markedDayCount, days: response.data.days };
};

/** Unmark whole work days in [from, to] for a subcontractor company. */
export const unmarkSubcontractorDayPayments = async (
  companyId: string,
  params: { from: string; to: string },
): Promise<{ unmarkedDayCount: number; days: string[] }> => {
  // Query string: makeApiCall does not attach a body on DELETE.
  const qs = new URLSearchParams({ from: params.from, to: params.to }).toString();
  const response = await makeApiCall<
    ItemEnvelope<{ companyId: string; from: string; to: string; unmarkedDayCount: number; days: string[] }>
  >('DELETE', `/subcontractor-companies/${companyId}/day-payments?${qs}`);
  return { unmarkedDayCount: response.data.unmarkedDayCount, days: response.data.days };
};
